// src/api/roadmapApi.js
// Recommendations & roadmap — the managed data behind the report's last section.
//
// Two sources, one view: the client's budgeted one-off project/hardware lines
// for the financial year, and recommendations (AI-drafted or hand-written).
//
// ⛔ A RECOMMENDATION REACHES THE CLIENT REPORT ONLY ONCE APPROVED. `draft()`
// stores UNAPPROVED drafts for editing and nothing more — approving is the
// separate `updateRec(..., { approved: true })` call. Storing is not publishing.
//
// All calls go through apiClient so the MSAL Bearer token is attached.
import apiClient from "./apiClient";

const BASE = "/api/roadmap";

/** The full in-app view: budget items with their state, plus every rec incl. drafts. */
export async function getRoadmap(companyId, fy) {
  const res = await apiClient.get(`${BASE}/${companyId}`, { params: fy ? { fy } : {} });
  return res.data.data;
}

/**
 * Promote a budgeted line to a scheduled project, and/or set its month.
 *
 * ⚠ Pass `targetMonth: null` to CLEAR a month; omit the key to leave it alone.
 * The backend distinguishes the two, so sending "" or undefined for a clear
 * silently does nothing.
 */
export async function setItemState(companyId, lineId, { status, targetMonth } = {}) {
  const body = {};
  if (status !== undefined) body.status = status;
  if (targetMonth !== undefined) body.targetMonth = targetMonth;
  const res = await apiClient.put(`${BASE}/${companyId}/item/${lineId}`, body);
  return res.data.data;
}

export async function addRec(companyId, rec) {
  const res = await apiClient.post(`${BASE}/${companyId}/recs`, rec);
  return res.data.data;
}

export async function updateRec(companyId, id, patch) {
  const res = await apiClient.put(`${BASE}/${companyId}/recs/${id}`, patch);
  return res.data.data;
}

export async function deleteRec(companyId, id) {
  const res = await apiClient.delete(`${BASE}/${companyId}/recs/${id}`);
  return res.data;
}

/**
 * Draft recommendations from the month's real report figures.
 *
 * ⚠ Slow — it assembles the whole report model server-side so the suggestions
 * are grounded in the same data the client will see, rather than in anything
 * the browser passes in.
 */
export async function draftRecs(companyId, period) {
  const res = await apiClient.post(`${BASE}/${companyId}/draft/${period}`, {}, { timeout: 120000 });
  return res.data;
}

/** Freeze the roadmap against a month. /generate does this too. */
export async function freezeRoadmap(companyId, period) {
  const res = await apiClient.post(`${BASE}/${companyId}/freeze/${period}`, {});
  return res.data.data;
}

/**
 * THE ONE shared create-quote action — called from BOTH surfaces (the Monthly
 * Reports roadmap and the Meeting Notes section), so they cannot drift.
 *
 * ⚠ THE QUOTE RECORD IS MINTED BY THE CALLER and its id posted here. Quotes live
 * only in the per-user app-state blob, which the SPA overwrites whole on a
 * debounced PUT — a quote written from the server would be discarded by the next
 * save. So the caller creates it through the one quote factory; the server owns
 * everything durable: the link, the state flip, the email and the guard.
 *
 * ⛔ IDEMPOTENT. If the item is already quoted the response carries
 * `alreadyQuoted: true` and the ORIGINAL `linkedQuoteId`, and no second email is
 * sent. The caller must then DISCARD the record it optimistically minted rather
 * than adding a duplicate quote to state.
 */
export async function createQuoteForItem(companyId, itemId, { quoteId, quoteNumber, companyName, appUrl } = {}) {
  const res = await apiClient.post(
    `${BASE}/${companyId}/items/${encodeURIComponent(itemId)}/create-quote`,
    { quoteId, quoteNumber, companyName, appUrl },
    { timeout: 60000 },
  );
  return res.data;
}
